import { compact } from 'lodash-es';
import { RevenueReportAggregateEnum, RevenueReportSortColumnId } from 'anchor-scout-common/types';
import { SearchFilterType } from '../../../components/TableFilters/types';
import { Breakdown, FilterType } from './types';
export const defaultAggregate = RevenueReportAggregateEnum.Month;
export const makeCsv = (param)=>{
    let { headerRow, entries, showPeriod, showCustomer, showService } = param;
    return [
        headerRow
    ].concat(entries.map((entry)=>{
        return compact([
            showPeriod && entry.formattedDay,
            showCustomer && entry.customer,
            showService && entry.service,
            entry.rawRevenue.toString()
        ]);
    }));
};
export const aggregateOptions = (texts)=>[
        {
            value: RevenueReportAggregateEnum.Day,
            text: texts.day()
        },
        {
            value: RevenueReportAggregateEnum.Week,
            text: texts.week()
        },
        {
            value: RevenueReportAggregateEnum.Month,
            text: texts.month()
        },
        {
            value: RevenueReportAggregateEnum.Quarter,
            text: texts.quarter()
        },
        {
            value: RevenueReportAggregateEnum.Year,
            text: texts.year()
        },
        {
            value: RevenueReportAggregateEnum.AllTime,
            text: texts.allTime()
        }
    ];
export const aggregateNames = (texts)=>({
        [RevenueReportAggregateEnum.Day]: texts.day(),
        [RevenueReportAggregateEnum.Week]: texts.week(),
        [RevenueReportAggregateEnum.Month]: texts.month(),
        [RevenueReportAggregateEnum.Quarter]: texts.quarter(),
        [RevenueReportAggregateEnum.Year]: texts.year(),
        [RevenueReportAggregateEnum.AllTime]: texts.allTime()
    });
export const breakdownNames = (texts)=>({
        [Breakdown.Service]: texts.serviceName(),
        [Breakdown.Customer]: texts.companyName()
    });
export const breakdownOptions = (texts)=>[
        {
            value: Breakdown.Service,
            text: breakdownNames(texts)[Breakdown.Service]
        },
        {
            value: Breakdown.Customer,
            text: breakdownNames(texts)[Breakdown.Customer]
        }
    ];
export const makeFiltersConfig = (filterOptions, texts)=>({
        [FilterType.CustomerName]: {
            type: SearchFilterType.MultiSelectFilter,
            options: filterOptions[FilterType.CustomerName] || [],
            removable: true,
            withSearchbox: true
        },
        [FilterType.ServiceName]: {
            type: SearchFilterType.MultiSelectFilter,
            options: filterOptions[FilterType.ServiceName] || [],
            removable: true,
            withSearchbox: true
        },
        [FilterType.DateRange]: {
            type: SearchFilterType.DateRangeWithPresets,
            removable: false
        },
        [FilterType.Aggregate]: {
            type: SearchFilterType.RadioFilter,
            title: texts.timeInterval(),
            options: aggregateOptions(texts).map((param)=>{
                let { value, text } = param;
                return {
                    value,
                    label: text
                };
            }),
            defaultOption: defaultAggregate,
            removable: false
        },
        [FilterType.Breakdowns]: {
            type: SearchFilterType.MultiSelectFilter,
            title: texts.breakdownBy(),
            options: breakdownOptions(texts).map((param)=>{
                let { value, text } = param;
                return {
                    value,
                    label: text
                };
            }),
            removable: false,
            selectAllByDefault: false
        }
    });
const makeServiceNameHeaderItem = (texts)=>({
        sortId: RevenueReportSortColumnId.ServiceName,
        value: texts.serviceName()
    });
const makeCompanyNameHeaderItem = (texts)=>({
        sortId: RevenueReportSortColumnId.CompanyName,
        value: texts.companyName()
    });
const makeAggregateHeaderItem = (aggregate)=>({
        sortId: RevenueReportSortColumnId.Aggregate,
        value: aggregate
    });
const makeRevenueHeaderItem = (texts)=>({
        sortId: RevenueReportSortColumnId.Revenue,
        value: texts.revenue()
    });
export const revenueReportHeaderColumnDefs = {
    ['']: (aggregate, texts)=>compact([
            aggregate !== RevenueReportAggregateEnum.AllTime && makeAggregateHeaderItem(aggregateNames(texts)[aggregate]),
            makeRevenueHeaderItem(texts)
        ]),
    [Breakdown.Service]: (aggregate, texts)=>compact([
            aggregate !== RevenueReportAggregateEnum.AllTime && makeAggregateHeaderItem(aggregateNames(texts)[aggregate]),
            makeServiceNameHeaderItem(texts),
            makeRevenueHeaderItem(texts)
        ]),
    [Breakdown.Customer]: (aggregate, texts)=>compact([
            aggregate !== RevenueReportAggregateEnum.AllTime && makeAggregateHeaderItem(aggregateNames(texts)[aggregate]),
            makeCompanyNameHeaderItem(texts),
            makeRevenueHeaderItem(texts)
        ]),
    [[
        Breakdown.Customer,
        Breakdown.Service
    ].join(',')]: (aggregate, texts)=>compact([
            aggregate !== RevenueReportAggregateEnum.AllTime && makeAggregateHeaderItem(aggregateNames(texts)[aggregate]),
            makeCompanyNameHeaderItem(texts),
            makeServiceNameHeaderItem(texts),
            makeRevenueHeaderItem(texts)
        ])
};
export const revenueReportColumnDefs = {
    ['']: (aggregate)=>compact([
            aggregate !== RevenueReportAggregateEnum.AllTime && {
                width: 476,
                sortable: true,
                bi: {
                    name: 'sort_revenue_breakdown_by_period'
                }
            },
            {
                width: 476,
                sortable: true,
                hAlign: 'end',
                bi: {
                    name: 'sort_revenue_breakdown_by_revenue'
                }
            }
        ]),
    [Breakdown.Service]: (aggregate)=>compact([
            aggregate !== RevenueReportAggregateEnum.AllTime && {
                width: 317,
                sortable: true,
                bi: {
                    name: 'sort_revenue_breakdown_service_by_period'
                }
            },
            {
                width: 317,
                sortable: true,
                bi: {
                    name: 'sort_revenue_breakdown_service_by_service_name'
                }
            },
            {
                width: 318,
                sortable: true,
                hAlign: 'end',
                bi: {
                    name: 'sort_revenue_breakdown_service_by_revenue'
                }
            }
        ]),
    [Breakdown.Customer]: (aggregate)=>compact([
            aggregate !== RevenueReportAggregateEnum.AllTime && {
                width: 317,
                sortable: true,
                bi: {
                    name: 'sort_revenue_breakdown_customer_by_period'
                }
            },
            {
                width: 317,
                sortable: true,
                bi: {
                    name: 'sort_revenue_breakdown_customer_by_company_name'
                }
            },
            {
                width: 318,
                sortable: true,
                hAlign: 'end',
                bi: {
                    name: 'sort_revenue_breakdown_customer_by_revenue'
                }
            }
        ]),
    [[
        Breakdown.Customer,
        Breakdown.Service
    ].join(',')]: (aggregate)=>compact([
            aggregate !== RevenueReportAggregateEnum.AllTime && {
                width: 238,
                sortable: true,
                bi: {
                    name: 'sort_revenue_breakdown_customer_service_by_period'
                }
            },
            {
                width: 238,
                sortable: true,
                bi: {
                    name: 'sort_revenue_breakdown_customer_service_by_company_name'
                }
            },
            {
                width: 238,
                sortable: true,
                bi: {
                    name: 'sort_revenue_breakdown_customer_service_by_service_name'
                }
            },
            {
                width: 238,
                sortable: true,
                hAlign: 'end',
                bi: {
                    name: 'sort_revenue_breakdown_customer_service_by_revenue'
                }
            }
        ])
};
export const filterLabels = (texts)=>({
        [FilterType.CustomerName]: texts.companyName(),
        [FilterType.ServiceName]: texts.serviceName(),
        [FilterType.DateRange]: texts.dateRange(),
        [FilterType.Aggregate]: aggregateNames(texts)[defaultAggregate],
        [FilterType.Breakdowns]: texts.breakdowns()
    });
export const getDefaultSort = (sortIds)=>({
        colIndex: sortIds.indexOf(RevenueReportSortColumnId.Aggregate),
        sort: 'desc'
    });
export const getHeaderItems = (breakdowns, aggregate, texts)=>revenueReportHeaderColumnDefs[breakdowns.sort().join(',')](aggregate, texts);
